import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import type { AppNotification, NotificationCategory } from "@/types";

const CATEGORY_DOT: Record<NotificationCategory, string> = {
  registration: "bg-blue-500",
  payment: "bg-emerald-500",
  club: "bg-purple-500",
  progress: "bg-amber-500",
  membership: "bg-pink-500",
  system: "bg-gray-400",
};

function relativeTime(d?: Date | { toDate?: () => Date } | null): string {
  if (!d) return "";
  const date =
    d instanceof Date
      ? d
      : typeof (d as any).toDate === "function"
        ? (d as any).toDate()
        : new Date(d as any);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `hace ${Math.floor(diff / 86400)} d`;
  return date.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
}

export default function NotificationBell() {
  const { items, unread, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleClick = async (n: AppNotification) => {
    if (!n.read) {
      try {
        await markRead(n.id);
      } catch (err) {
        console.warn("markRead failed", err);
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead();
    } catch (err) {
      console.warn("markAllRead failed", err);
    }
  };

  const recent = items.slice(0, 8);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-gray-200/60 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-800">
                Notificaciones
              </span>
              {unread > 0 && (
                <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {unread} nuevas
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Marcar todas
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No tienes notificaciones aún</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recent.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(n)}
                      className={cn(
                        "w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-3",
                        !n.read && "bg-purple-50/40",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1.5 w-2 h-2 rounded-full shrink-0",
                          CATEGORY_DOT[n.category] || "bg-gray-400",
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-sm leading-snug truncate",
                            n.read
                              ? "text-gray-700"
                              : "text-gray-900 font-semibold",
                          )}
                          title={n.title}
                        >
                          {n.title}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                          {n.body}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {relativeTime(n.createdAt as any)}
                        </p>
                      </div>
                      {!n.read && (
                        <Check className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-1" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs font-medium text-purple-600 hover:text-purple-700"
            >
              Ver todas →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
